/**
 * Time and interval helpers for payroll calculation.
 *
 * Extracted from payrollService so the arithmetic can be unit tested on its
 * own. Every function here is pure. Shifts cross midnight and DST boundaries
 * routinely, so all day/window attribution runs through the site's IANA zone
 * rather than the browser's.
 */

export const MS_PER_MINUTE = 60000;
export const MINUTES_PER_DAY = 24 * 60;

export interface TimeInterval {
  start: Date;
  end: Date;
}

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekdayIndex: number;
  dateKey: string;
}

export function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function minutesBetween(start: Date, end: Date): number {
  return Math.max((end.getTime() - start.getTime()) / MS_PER_MINUTE, 0);
}

export function intervalMinutes(intervals: TimeInterval[]): number {
  return intervals.reduce((total, interval) => total + minutesBetween(interval.start, interval.end), 0);
}

export function normaliseInterval(
  startValue: string | null | undefined,
  endValue: string | null | undefined,
): TimeInterval | null {
  const start = toDate(startValue);
  const end = toDate(endValue);
  if (!start || !end || end.getTime() <= start.getTime()) return null;
  return { start, end };
}

export function subtractIntervals(baseIntervals: TimeInterval[], deductions: TimeInterval[]): TimeInterval[] {
  const sortedDeductions = deductions
    .slice()
    .sort((left, right) => left.start.getTime() - right.start.getTime());

  let remaining = baseIntervals.slice();

  sortedDeductions.forEach((deduction) => {
    const next: TimeInterval[] = [];

    remaining.forEach((interval) => {
      const startMs = interval.start.getTime();
      const endMs = interval.end.getTime();
      const deductionStartMs = Math.max(deduction.start.getTime(), startMs);
      const deductionEndMs = Math.min(deduction.end.getTime(), endMs);

      if (deductionEndMs <= deductionStartMs) {
        next.push(interval);
        return;
      }

      if (deductionStartMs > startMs) {
        next.push({ start: interval.start, end: new Date(deductionStartMs) });
      }
      if (deductionEndMs < endMs) {
        next.push({ start: new Date(deductionEndMs), end: interval.end });
      }
    });

    remaining = next;
  });

  return remaining;
}

export function intersectIntervals(intervals: TimeInterval[], windows: TimeInterval[]): TimeInterval[] {
  const intersections: TimeInterval[] = [];

  intervals.forEach((interval) => {
    windows.forEach((window) => {
      const startMs = Math.max(interval.start.getTime(), window.start.getTime());
      const endMs = Math.min(interval.end.getTime(), window.end.getTime());

      if (endMs > startMs) {
        intersections.push({ start: new Date(startMs), end: new Date(endMs) });
      }
    });
  });

  return intersections;
}

/** Return a sorted union so overlapping time records are never counted twice. */
export function mergeIntervals(intervals: TimeInterval[]): TimeInterval[] {
  const sorted = intervals
    .filter((interval) => interval.end > interval.start)
    .slice()
    .sort((left, right) => left.start.getTime() - right.start.getTime());
  const merged: TimeInterval[] = [];

  sorted.forEach((interval) => {
    const previous = merged[merged.length - 1];
    if (!previous || interval.start > previous.end) {
      merged.push({ start: new Date(interval.start), end: new Date(interval.end) });
      return;
    }
    if (interval.end > previous.end) previous.end = new Date(interval.end);
  });

  return merged;
}

const zonedFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getZonedFormatter(timeZone: string): Intl.DateTimeFormat {
  if (!zonedFormatterCache.has(timeZone)) {
    zonedFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'short',
        hourCycle: 'h23',
      }),
    );
  }

  return zonedFormatterCache.get(timeZone)!;
}

export function getZonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = Object.fromEntries(
    getZonedFormatter(timeZone)
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );

  const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday ?? '');

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekdayIndex: weekdayIndex >= 0 ? weekdayIndex : 0,
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
  };
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = getZonedParts(date, timeZone);
  const localAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  return localAsUtc - date.getTime();
}

export function splitDateKey(dateKey: string): [number, number, number] {
  const [year, month, day] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  return [year, month, day];
}

export function formatDateKey(date: Date): string {
  return [
    String(date.getUTCFullYear()),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

export function addDaysToDateKey(dateKey: string, days: number): string {
  const [year, month, day] = splitDateKey(dateKey);
  return formatDateKey(new Date(Date.UTC(year, month - 1, day + days)));
}

export function compareDateKeys(left: string, right: string): number {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/**
 * Convert a wall-clock minute offset within a local date to an absolute
 * instant. Iterates because the offset itself depends on the instant: on a DST
 * boundary the first guess can land in the wrong offset.
 */
export function zonedDateTimeToUtc(dateKey: string, absoluteMinute: number, timeZone: string): Date {
  const [year, month, day] = splitDateKey(dateKey);
  const dayOffset = Math.floor(absoluteMinute / MINUTES_PER_DAY);
  const minuteOfDay = absoluteMinute - (dayOffset * MINUTES_PER_DAY);
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;
  const localAsUtc = Date.UTC(year, month - 1, day + dayOffset, hour, minute);
  let utcMs = localAsUtc;

  for (let index = 0; index < 4; index += 1) {
    const offset = getTimeZoneOffsetMs(new Date(utcMs), timeZone);
    utcMs = localAsUtc - offset;
  }

  return new Date(utcMs);
}

export function overlapMinutes(interval: TimeInterval, windowStart: Date, windowEnd: Date): number {
  const startMs = Math.max(interval.start.getTime(), windowStart.getTime());
  const endMs = Math.min(interval.end.getTime(), windowEnd.getTime());
  return Math.max((endMs - startMs) / MS_PER_MINUTE, 0);
}

export function getWeekdayForDateKey(dateKey: string, timeZone: string): number {
  return getZonedParts(zonedDateTimeToUtc(dateKey, 12 * 60, timeZone), timeZone).weekdayIndex;
}

/** Total minutes of the intervals falling on local days matching the predicate. */
export function sumMinutesForLocalDays(
  intervals: TimeInterval[],
  timeZone: string,
  predicate: (dateKey: string, weekdayIndex: number) => boolean,
): number {
  let total = 0;

  intervals.forEach((interval) => {
    let dateKey = getZonedParts(interval.start, timeZone).dateKey;
    const endKey = getZonedParts(interval.end, timeZone).dateKey;

    while (compareDateKeys(dateKey, endKey) <= 0) {
      if (predicate(dateKey, getWeekdayForDateKey(dateKey, timeZone))) {
        total += overlapMinutes(
          interval,
          zonedDateTimeToUtc(dateKey, 0, timeZone),
          zonedDateTimeToUtc(dateKey, MINUTES_PER_DAY, timeZone),
        );
      }
      dateKey = addDaysToDateKey(dateKey, 1);
    }
  });

  return Math.round(total);
}

/**
 * Total minutes falling inside a recurring local time-of-day window. The window
 * may run past midnight (e.g. 21:00 to 07:00 is start 1260, end 1860), so the
 * scan starts a day early to catch a window opened by the previous date.
 */
export function sumMinutesForLocalWindow(
  intervals: TimeInterval[],
  timeZone: string,
  startMinute: number,
  endMinute: number,
): number {
  let total = 0;

  intervals.forEach((interval) => {
    let dateKey = addDaysToDateKey(getZonedParts(interval.start, timeZone).dateKey, -1);
    const endKey = getZonedParts(interval.end, timeZone).dateKey;

    while (compareDateKeys(dateKey, endKey) <= 0) {
      total += overlapMinutes(
        interval,
        zonedDateTimeToUtc(dateKey, startMinute, timeZone),
        zonedDateTimeToUtc(dateKey, endMinute, timeZone),
      );
      dateKey = addDaysToDateKey(dateKey, 1);
    }
  });

  return Math.round(total);
}

/** Local date key the shift's paid time is attributed to, for daily overtime. */
export function getAttributionDateKey(interval: TimeInterval, timeZone: string): string {
  return getZonedParts(interval.start, timeZone).dateKey;
}

/**
 * Date key of the week start containing `dateKey`, where weekStartWeekday is
 * 0 (Sunday) through 6 (Saturday). Used to bucket weekly overtime.
 */
export function getWeekStartDateKey(dateKey: string, timeZone: string, weekStartWeekday: number): string {
  const weekday = getWeekdayForDateKey(dateKey, timeZone);
  const delta = (weekday - weekStartWeekday + 7) % 7;
  return addDaysToDateKey(dateKey, -delta);
}

export function hoursFromMinutes(minutes: number): number {
  // Keep full minute precision internally. UI and CSV formatters round only at
  // presentation time; otherwise many short rows accumulate material drift
  // (for example 60 one-minute rows used to total 1.20 rather than 1.00).
  return minutes / 60;
}

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
